import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import Cookies from 'js-cookie';
import { AuthContext } from '../../contexts/AuthContext';
import { SessionTimeoutModal } from '../SessionTimeoutModal';
import { AccountDisableModal } from '../AccountDisableModal';
import { useToast } from '../../contexts/ToastContext';
import authService from '../../services/authService';
import { merchantService } from '../../services/merchantService';
import { tokenStorage } from '../../utils/tokenStorage';
import logger from '../../utils/logger';
import { updateFaviconForRole } from '../../utils/favicon';
import { refreshTokensSingleFlight } from '../../lib/tokenRefresh';
import { walletService } from '../../services/walletService';
import type {
  User,
  LoginRequest,
  RegisterRequest,
  SendOTPRequest,
  VerifyOTPRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UserApiResponse,
  SwitchRoleResponse,
} from '../../types/auth';

interface LoginAttempt {
  timestamp: number;
  success: boolean;
  error?: string;
}

interface NetworkStatus {
  isOnline: boolean;
  lastChecked: number;
  error: string | null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [refreshAttempts, setRefreshAttempts] = useState(0);
  const [loginAttempts, setLoginAttempts] = useState<LoginAttempt[]>([]);
  const [networkStatus, setNetworkStatus] = useState<NetworkStatus>({
    isOnline: navigator.onLine,
    lastChecked: Date.now(),
    error: null,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Session timeout modal states
  const [showSessionTimeoutModal, setShowSessionTimeoutModal] = useState(false);
  const [sessionTimeoutReason, setSessionTimeoutReason] = useState<
    'refresh_failed' | 'token_expired' | 'session_expired' | 'other_device_login'
  >('refresh_failed');

  // Account disable modal states
  const [showAccountDisableModal, setShowAccountDisableModal] = useState(false);
  const [accountDisableReason, setAccountDisableReason] = useState<string | undefined>(undefined);

  // Toast hook
  const { showSuccess, showError } = useToast();

  const MAX_REFRESH_ATTEMPTS = 3;
  const MAX_LOGIN_ATTEMPTS = 5;
  const LOGIN_ATTEMPT_WINDOW = 15 * 60 * 1000; // 15 minutes
  const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes

  // Network status monitoring
  useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus({ isOnline: true, lastChecked: Date.now(), error: null });
      logger.log('Network connection restored');
    };

    const handleOffline = () => {
      setNetworkStatus({
        isOnline: false,
        lastChecked: Date.now(),
        error: 'Network connection lost',
      });
      logger.log('Network connection lost');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check for post-reload toast messages
  useEffect(() => {
    const postReloadMessage = localStorage.getItem('postReloadToast');
    if (postReloadMessage) {
      try {
        const { type, message } = JSON.parse(postReloadMessage);
        if (type === 'success') {
          showSuccess(message);
        } else if (type === 'error') {
          showError(message);
        }
      } catch (error) {
        logger.error('Failed to parse post-reload toast message:', error);
      }
      // Clean up the message after showing it
      localStorage.removeItem('postReloadToast');
    }
  }, [showSuccess, showError]);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => setLastActivity(Date.now());

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach((event) => {
      document.addEventListener(event, updateActivity, true);
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, updateActivity, true);
      });
    };
  }, []);

  // Maintain a stable ref to logout to avoid dependency/order issues
  const logoutRef = useRef<((reason?: string) => Promise<void> | void) | null>(null);

  // Refresh wallet balance - Define it early so it can be used by other functions
  const refreshBalance = useCallback(async () => {
    // Note: We check isAuthenticated inside, but during auth flow we might call this
    // before state updates, so we might need to rely on token presence.
    // However, usually we call this after successful token refresh.
    if (!tokenStorage.getAccessToken()) return;

    try {
      const response = await walletService.getWalletBalance();
      if (response.success && response.data) {
        console.log('[AuthProvider] Balance fetched:', response.data.balances.MMK);
        setUser((prev) => {
          if (!prev) {
            console.log('[AuthProvider] No prev user to update balance');
            return null;
          }
          console.log(
            '[AuthProvider] Updating user balance:',
            response.data.balances.MMK.available,
          );
          return {
            ...prev,
            walletBalance: response.data.balances.MMK.available,
            balances: response.data.balances,
          };
        });
      }
    } catch {
      // logger.error('Failed to refresh balance:', error);
      // Suppress error logging for balance specific failures to avoid noise
    }
  }, []);

  // Primary token refresh function - the core authentication mechanism
  const refreshToken = useCallback(
    async (showUserConfirmation: boolean = false): Promise<boolean> => {
      // Prevent concurrent refresh attempts
      if (isRefreshing) {
        logger.log('🔄 Token refresh already in progress, waiting...');
        return false;
      }

      logger.log('🚀 Starting token refresh process...');
      setIsRefreshing(true);

      try {
        // Step 1: Validate refresh token exists and is valid
        const refreshTokenValue = tokenStorage.getRefreshToken();
        logger.log('📋 Refresh token check:', {
          hasToken: !!refreshTokenValue,
          tokenLength: refreshTokenValue?.length || 0,
        });

        if (!refreshTokenValue) {
          logger.error('❌ No refresh token available - forcing logout');
          if (logoutRef.current) {
            await logoutRef.current();
          }
          return false;
        }

        // Step 2: Check refresh token expiry
        // Do not force logout based on client-side decode.
        // Some backends issue opaque/non-JWT refresh tokens.
        // Proceed to server refresh call and let server decide validity.

        // Step 3: Network connectivity check
        if (!networkStatus.isOnline) {
          logger.warn('⚠️ No network connection for token refresh');
          if (showUserConfirmation) {
            const retry = window.confirm(
              'Network connection is required to refresh your session. Would you like to retry?',
            );
            if (retry) {
              logger.log('🔄 User requested retry, waiting 2 seconds...');
              await new Promise((resolve) => setTimeout(resolve, 2000));
              return await refreshToken(false);
            }
          }
          return false;
        }

        // Step 4: Make refresh API call via shared orchestrator
        logger.log('📡 Making refresh token API call (orchestrated)...');
        const {
          accessToken,
          refreshToken: newRefreshToken,
          user: userData,
        } = await refreshTokensSingleFlight();

        logger.log('📥 Refresh response received via orchestrator:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!newRefreshToken,
          hasUserData: !!userData,
        });

        // Get current user data if no new user data provided
        const currentUser = userData || tokenStorage.getUserData();

        if (!currentUser) {
          logger.error('❌ No user data available after refresh');
          if (logoutRef.current) {
            await logoutRef.current();
          }
          return false;
        }

        // Step 5: Orchestrator already persisted tokens and cookies; update app state
        logger.log('🎉 Token refresh completed successfully');
        setRefreshAttempts(0);

        // Update user state if new user data is provided
        if (userData) {
          setUser({
            id: userData.id || userData._id || '',
            name: userData.name,
            email: userData.email || '',
            phoneNumber: userData.phoneNumber,
            activeRole: userData.activeRole || 'user',
            merchantId: userData.merchantId || undefined,
            walletBalance: (userData as UserApiResponse).walletBalance,
          });
          logger.log('👤 User data updated from refresh response');
        }

        // Always try to refresh balance after token refresh
        refreshBalance();

        return true;
      } catch (error) {
        logger.error('💥 Token refresh failed:', error);

        // If network error or offline, do not force logout; allow retry
        const axiosError = error as Partial<{ response: unknown; code?: string }>;
        const isNetworkErr =
          !axiosError.response && (axiosError.code === 'ERR_NETWORK' || !navigator.onLine);
        if (isNetworkErr) {
          logger.warn('Network error during refresh; preserving session for later retry');
          setIsRefreshing(false);
          return false;
        }

        // Check if it's a 401 error (token expired) - don't retry, just logout
        if (error instanceof Error && error.message.includes('401')) {
          logger.error('🚫 Refresh token expired (401) - forcing logout');
          if (logoutRef.current) {
            await logoutRef.current('token_expired');
          }
          setSessionTimeoutReason('token_expired');
          setShowSessionTimeoutModal(true);
          return false;
        }

        const newAttempts = refreshAttempts + 1;
        setRefreshAttempts(newAttempts);

        if (newAttempts >= MAX_REFRESH_ATTEMPTS) {
          logger.error(
            `🚫 Token refresh failed after ${MAX_REFRESH_ATTEMPTS} attempts - giving up`,
          );

          if (showUserConfirmation) {
            if (logoutRef.current) {
              await logoutRef.current('refresh_failed');
            }
            setSessionTimeoutReason('refresh_failed');
            setShowSessionTimeoutModal(true);
          } else {
            logger.log('🔄 Auto-logout due to refresh failure');
            if (logoutRef.current) {
              await logoutRef.current('refresh_failed');
            }
            setSessionTimeoutReason('refresh_failed');
            setShowSessionTimeoutModal(true);
          }
          return false;
        }

        // For other errors, wait briefly and allow a retry path without forcing logout
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setIsRefreshing(false);
        return false;
      } finally {
        setIsRefreshing(false);
      }
    },
    [isRefreshing, refreshAttempts, networkStatus.isOnline, refreshBalance],
  );

  // Make authenticated requests with automatic token refresh
  const makeAuthenticatedRequest = useCallback(
    async (url: string, options: RequestInit = {}): Promise<Response> => {
      const accessToken = tokenStorage.getAccessToken();

      if (!accessToken) {
        throw new Error('No access token available');
      }

      // Check if token is about to expire and refresh if needed
      if (tokenStorage.isTokenAboutToExpire(accessToken, 5)) {
        logger.log('Access token is about to expire, refreshing...');
        await refreshToken();
      }

      // Add authorization header
      const headers = {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        ...options.headers,
      };

      try {
        const response = await fetch(url, {
          ...options,
          headers,
        });

        // If we get a 401, try to refresh the token once
        if (response.status === 401) {
          logger.log('Request failed with 401, attempting token refresh...');
          const refreshed = await refreshToken();
          if (refreshed) {
            const newToken = tokenStorage.getAccessToken();
            return fetch(url, {
              ...options,
              headers: {
                ...headers,
                Authorization: `Bearer ${newToken}`,
              },
            });
          }
        }

        return response;
      } catch (error) {
        logger.error('Request failed:', error);
        throw error;
      }
    },
    [refreshToken],
  );

  // Enhanced logout
  const logout = useCallback(
    async (reason?: string) => {
      try {
        // Get current state values directly to avoid dependencies
        const currentUser = user;
        const currentIsAuthenticated = isAuthenticated;

        logger.log('🚪 Logout initiated:', {
          reason: reason || 'Manual logout',
          timestamp: new Date().toISOString(),
          currentUser: currentUser?.name || 'Unknown',
          userId: currentUser?.id || 'Unknown',
          wasAuthenticated: currentIsAuthenticated,
        });

        // Log current token status before logout
        const accessToken = tokenStorage.getAccessToken();
        const refreshTokenValue = tokenStorage.getRefreshToken();

        if (accessToken) {
          const accessTokenTimeLeft = tokenStorage.getTimeUntilExpirySync(accessToken);
          logger.log('📊 Access token status at logout:', {
            isValid: tokenStorage.isValidToken(accessToken),
            isExpired: tokenStorage.isTokenExpired(accessToken),
            timeLeftMs: accessTokenTimeLeft,
            timeLeftMinutes: Math.round(accessTokenTimeLeft / 60000),
          });
        }

        if (refreshTokenValue) {
          const refreshTokenTimeLeft = tokenStorage.getTimeUntilExpirySync(refreshTokenValue);
          logger.log('📊 Refresh token status at logout:', {
            isValid: tokenStorage.isValidToken(refreshTokenValue),
            isExpired: tokenStorage.isTokenExpired(refreshTokenValue),
            timeLeftMs: refreshTokenTimeLeft,
            timeLeftDays: Math.round(refreshTokenTimeLeft / (24 * 60 * 60 * 1000)),
          });
        }

        setIsAuthenticated(false);
        setUser(null);

        // Clear all authentication data
        await tokenStorage.clearTokens();

        // Clear cookies
        Cookies.remove('accessToken', { path: '/' });
        Cookies.remove('refreshToken', { path: '/' });
        Cookies.remove('gid', { path: '/' });

        logger.log('✅ Logout completed successfully');
      } catch (error) {
        logger.error('❌ Error during logout:', error);
      }
    },
    [isAuthenticated, user],
  ); // Include dependencies as required by ESLint

  // Keep logout ref in sync
  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  // Token refresh interval management
  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    let timeoutId: NodeJS.Timeout;

    const scheduleTokenRefresh = async () => {
      if (!isAuthenticated) return;

      const accessToken = tokenStorage.getAccessToken();
      const refreshTokenValue = tokenStorage.getRefreshToken();

      // Check if both tokens are available
      if (!accessToken || !refreshTokenValue) {
        logger.log('Missing tokens detected; attempting recovery before logout');
        try {
          const recovered = await tokenStorage.checkAndRepairData();
          const repairedAccess = tokenStorage.getAccessToken();
          const repairedRefresh = tokenStorage.getRefreshToken();

          if (recovered && repairedAccess && repairedRefresh) {
            logger.log('Token recovery successful; resuming session');
          } else {
            const cookieRefresh = Cookies.get('refreshToken');
            if (cookieRefresh) {
              logger.log('Found refresh token in cookie; initiating refresh');
              await refreshToken();
              return;
            }
            logger.log('Recovery failed, logging out');
            if (logoutRef.current) {
              void logoutRef.current();
            }
            return;
          }
        } catch (err) {
          logger.error('Recovery attempt errored; logging out', err);
          if (logoutRef.current) {
            void logoutRef.current();
          }
          return;
        }
      }

      // Remove activity timeout check to allow logout even with valid refresh token

      // Check if token is already expired
      const tokenForChecks = tokenStorage.getAccessToken();
      if (!tokenForChecks) {
        logger.log('No access token after recovery');
        return;
      }
      if (tokenStorage.isTokenExpired(tokenForChecks)) {
        logger.log('Access token expired, refreshing immediately');
        refreshToken();
        return;
      }

      // Calculate time until token expires
      const timeUntilExpiry = tokenStorage.getTimeUntilExpirySync(tokenForChecks);

      if (timeUntilExpiry > 0) {
        // Schedule refresh before token expires (with buffer)
        const refreshTime = Math.max(timeUntilExpiry - TOKEN_REFRESH_BUFFER, 1000); // At least 1 second
        logger.log(`⏰ Scheduling token refresh in ${Math.round(refreshTime / 1000)} seconds`);

        timeoutId = setTimeout(() => {
          logger.log('🔄 Scheduled token refresh triggered');
          // Check if refresh token is still available before refreshing
          const currentRefreshToken = tokenStorage.getRefreshToken();
          if (currentRefreshToken) {
            refreshToken();
          } else {
            logger.log('Refresh token no longer available, logging out');
            if (logoutRef.current) {
              void logoutRef.current();
            }
          }
        }, refreshTime);
      } else {
        // Token is already expired or about to expire, refresh immediately
        logger.log('🔄 Token expired or about to expire, refreshing immediately');
        refreshToken();
      }
    };

    if (isAuthenticated) {
      // Initial schedule
      scheduleTokenRefresh();

      // Set up regular interval as backup (every 10 minutes)
      intervalId = setInterval(scheduleTokenRefresh, 10 * 60 * 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isAuthenticated, TOKEN_REFRESH_BUFFER, lastActivity, refreshToken]);

  // Ensure refresh after app/tab resumes from background or device sleep
  useEffect(() => {
    const ensureRefreshOnResume = () => {
      if (!isAuthenticated) return;
      const access = tokenStorage.getAccessToken();
      if (
        !access ||
        tokenStorage.isTokenExpired(access) ||
        tokenStorage.isTokenAboutToExpire(access, 10)
      ) {
        logger.log('🔔 App resumed; ensuring token refresh');
        void refreshToken();
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        ensureRefreshOnResume();
      }
    };
    const handlePageShow = () => {
      ensureRefreshOnResume();
    };
    window.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [isAuthenticated, refreshToken]);

  // Enhanced authentication check on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        logger.log('🚀 Starting authentication check...');
        logger.log('📊 Auth check environment:', {
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          isOnline: navigator.onLine,
          localStorage: typeof localStorage !== 'undefined',
          sessionStorage: typeof sessionStorage !== 'undefined',
        });

        // Check and repair data if needed
        const dataValid = await tokenStorage.checkAndRepairData();
        if (!dataValid) {
          logger.log('❌ No valid authentication data found after recovery attempts');
          // If we still have a refresh token and user data, attempt a recovery via refresh
          const refreshTokenValue = tokenStorage.getRefreshToken();
          const userData = tokenStorage.getUserData();
          if (refreshTokenValue && userData) {
            logger.log(
              '🔄 Refresh token present; attempting session recovery via token refresh...',
            );
            const refreshed = await refreshToken();
            if (!refreshed) {
              setIsAuthenticated(false);
              setUser(null);
              setIsLoading(false);
              return;
            }
            logger.log('✅ Session recovered via refresh token');
            // Continue with normal flow below using refreshed tokens
          } else {
            setIsAuthenticated(false);
            setUser(null);
            setIsLoading(false);
            return;
          }
        }

        logger.log('✅ Data validation successful, retrieving tokens...');
        const accessToken = tokenStorage.getAccessToken();
        const refreshTokenValue = tokenStorage.getRefreshToken();
        const userData = tokenStorage.getUserData();

        logger.log('📋 Token retrieval results:', {
          hasAccessToken: !!accessToken,
          hasRefreshToken: !!refreshTokenValue,
          hasUserData: !!userData,
          accessTokenLength: accessToken?.length || 0,
          refreshTokenLength: refreshTokenValue?.length || 0,
        });

        // Enhanced token validation logging
        if (accessToken) {
          const isAccessTokenValid = tokenStorage.isValidToken(accessToken);
          const isAccessTokenExpired = tokenStorage.isTokenExpired(accessToken);
          const accessTokenTimeLeft = tokenStorage.getTimeUntilExpirySync(accessToken);

          logger.log('🔍 Access Token Analysis:', {
            isValid: isAccessTokenValid,
            isExpired: isAccessTokenExpired,
            timeLeftMs: accessTokenTimeLeft,
            timeLeftMinutes: Math.round(accessTokenTimeLeft / 60000),
            expiryDate: accessToken
              ? new Date(JSON.parse(atob(accessToken.split('.')[1])).exp * 1000).toISOString()
              : 'N/A',
          });
        }

        if (refreshTokenValue) {
          const isRefreshTokenValid = tokenStorage.isValidToken(refreshTokenValue);
          const isRefreshTokenExpired = tokenStorage.isTokenExpired(refreshTokenValue);
          const refreshTokenTimeLeft = tokenStorage.getTimeUntilExpirySync(refreshTokenValue);

          logger.log('🔍 Refresh Token Analysis:', {
            isValid: isRefreshTokenValid,
            isExpired: isRefreshTokenExpired,
            timeLeftMs: refreshTokenTimeLeft,
            timeLeftDays: Math.round(refreshTokenTimeLeft / (24 * 60 * 60 * 1000)),
            expiryDate: refreshTokenValue
              ? new Date(JSON.parse(atob(refreshTokenValue.split('.')[1])).exp * 1000).toISOString()
              : 'N/A',
          });
        }

        if (!accessToken || !refreshTokenValue) {
          logger.log('❌ Missing tokens after successful data validation');
          // This shouldn't happen if checkAndRepairData returned true
          // Let's try one more recovery attempt
          logger.log('Attempting emergency token recovery...');
          const emergencyRecovery = await tokenStorage.checkAndRepairData();
          if (!emergencyRecovery) {
            setIsAuthenticated(false);
            setUser(null);
            setIsLoading(false);
            return;
          }

          // Try to get tokens again
          const newAccessToken = tokenStorage.getAccessToken();
          const newRefreshToken = tokenStorage.getRefreshToken();
          // const newUserData = tokenStorage.getUserData();

          if (!newAccessToken || !newRefreshToken) {
            logger.log('❌ Emergency recovery failed');
            setIsAuthenticated(false);
            setUser(null);
            setIsLoading(false);
            return;
          }

          logger.log('✅ Emergency recovery successful');
          // Update the variables for the rest of the function
          // Note: We need to reassign to use the recovered tokens
          // const finalAccessToken = newAccessToken;
          // const finalRefreshToken = newRefreshToken;
          // const finalUserData = newUserData;
        }

        // Check if access token is expired
        const currentAccessToken = accessToken || tokenStorage.getAccessToken();
        if (currentAccessToken && tokenStorage.isTokenExpired(currentAccessToken)) {
          logger.log('⏰ Access token expired, attempting refresh...');
          const refreshed = await refreshToken();
          if (!refreshed) {
            logger.log('❌ Token refresh failed during auth check');
            setIsLoading(false);
            return;
          }
          logger.log('✅ Token refreshed successfully during auth check');
        }

        // Try to get fresh user profile
        try {
          logger.log('👤 Fetching fresh user profile...');
          const profileResponse = await authService.getUserProfile();

          if (profileResponse.success && profileResponse.data) {
            const freshUserData: User = {
              id: profileResponse.data.id || profileResponse.data._id || '',
              name: profileResponse.data.name,
              email: profileResponse.data.email || '',
              phoneNumber: profileResponse.data.phoneNumber,
              activeRole:
                ((profileResponse.data as UserApiResponse).activeRole as 'user' | 'merchant') ||
                'user',
              merchantId: (profileResponse.data as UserApiResponse).merchantId || undefined,
              walletBalance: (profileResponse.data as UserApiResponse).walletBalance,
            };

            logger.log('✅ Fresh user profile retrieved:', {
              userId: freshUserData.id,
              userName: freshUserData.name,
              hasEmail: !!freshUserData.email,
              hasPhone: !!freshUserData.phoneNumber,
            });

            setUser(freshUserData);
            setIsAuthenticated(true);

            // Update stored user data
            const currentTokens = {
              accessToken: tokenStorage.getAccessToken()!,
              refreshToken: tokenStorage.getRefreshToken()!,
            };
            if (freshUserData) {
              await tokenStorage.setTokens(
                currentTokens.accessToken,
                currentTokens.refreshToken,
                freshUserData,
              );
            }

            // Refresh balance after profile load
            refreshBalance();
          } else {
            throw new Error('Failed to fetch user profile');
          }
        } catch (error) {
          logger.warn('⚠️ Failed to fetch fresh profile, using cached data:', error);

          // Fallback to cached user data
          if (userData) {
            logger.log('📦 Using cached user data:', {
              userId: userData.id,
              userName: userData.name,
              hasEmail: !!userData.email,
              hasPhone: !!userData.phoneNumber,
            });

            setUser({
              id: userData.id,
              name: userData.name,
              email: userData.email,
              phoneNumber: userData.phoneNumber,
              activeRole:
                ((userData as UserApiResponse).activeRole as 'user' | 'merchant') || 'user',
              merchantId: (userData as UserApiResponse).merchantId || undefined,
              walletBalance: (userData as UserApiResponse).walletBalance,
            });
            setIsAuthenticated(true);
          } else {
            logger.log('❌ No cached user data available, logging out');
            if (logoutRef.current) {
              await logoutRef.current();
            }
          }
        }

        logger.log('🎉 Authentication check completed successfully');
      } catch (error) {
        logger.error('💥 Auth check failed:', error);
        const refreshTokenValue = tokenStorage.getRefreshToken();
        const userData = tokenStorage.getUserData();
        if (refreshTokenValue && userData) {
          const mappedActiveRole: 'user' | 'merchant' | undefined =
            userData.activeRole === 'merchant'
              ? 'merchant'
              : userData.activeRole === 'user'
                ? 'user'
                : 'user';

          setUser({
            id: userData.id,
            name: userData.name,
            email: userData.email,
            phoneNumber: userData.phoneNumber,
            activeRole: mappedActiveRole,
            merchantId: userData.merchantId || undefined,
          });
          setIsAuthenticated(true);
        } else {
          if (logoutRef.current) {
            await logoutRef.current();
          }
        }
      } finally {
        setIsLoading(false);
        logger.log('🏁 Auth check process finished');
      }
    };

    checkAuth();
    checkAuth();
  }, [refreshToken, refreshBalance]);

  // Enhanced login with attempt tracking
  const login = useCallback(
    async (
      accessToken: string,
      refreshToken: string,
      userData: {
        id?: string;
        _id?: string;
        name: string;
        email?: string;
        phoneNumber: string;
        role?: string;
      },
    ) => {
      try {
        // Map user data to match User type
        const mappedUserData: User = {
          id: userData.id || userData._id || '',
          name: userData.name,
          email: userData.email || '',
          phoneNumber: userData.phoneNumber,
          activeRole: ((userData as UserApiResponse).activeRole as 'user' | 'merchant') || 'user',
          merchantId: (userData as UserApiResponse).merchantId || undefined,
          walletBalance: (userData as UserApiResponse).walletBalance,
        };

        // Store tokens using enhanced storage
        await tokenStorage.setTokens(accessToken, refreshToken, mappedUserData);

        // Set cookies for backward compatibility
        // Set access token cookie to expire in 7 days to match refresh token
        Cookies.set('accessToken', accessToken, {
          secure: window.location.protocol === 'https:',
          sameSite: 'lax',
          expires: 7, // 7 days - matches refresh token expiry
          path: '/',
        });

        Cookies.set('refreshToken', refreshToken, {
          secure: window.location.protocol === 'https:',
          sameSite: 'lax',
          expires: 7, // 7 days
          path: '/',
        });

        Cookies.set('user', JSON.stringify(mappedUserData), {
          secure: window.location.protocol === 'https:',
          sameSite: 'lax',
          expires: 7,
          path: '/',
        });

        setUser(mappedUserData);
        setIsAuthenticated(true);
        setRefreshAttempts(0);

        // Record successful login attempt
        setLoginAttempts((prev) => [
          ...prev.filter((attempt) => Date.now() - attempt.timestamp < LOGIN_ATTEMPT_WINDOW),
          { timestamp: Date.now(), success: true },
        ]);

        // Refresh balance immediately after login
        refreshBalance();

        logger.log('Login successful');
      } catch (error) {
        logger.error('Login failed:', error);
        throw error;
      }
    },
    [LOGIN_ATTEMPT_WINDOW, refreshBalance],
  );

  // Enhanced login with credentials and attempt tracking
  const loginWithCredentials = useCallback(
    async (credentials: LoginRequest) => {
      // Check for too many failed attempts
      const recentFailedAttempts = loginAttempts.filter(
        (attempt) => Date.now() - attempt.timestamp < LOGIN_ATTEMPT_WINDOW && !attempt.success,
      );

      if (recentFailedAttempts.length >= MAX_LOGIN_ATTEMPTS) {
        const error = `Too many failed login attempts. Please try again later.`;
        setLoginAttempts((prev) => [
          ...prev.filter((attempt) => Date.now() - attempt.timestamp < LOGIN_ATTEMPT_WINDOW),
          { timestamp: Date.now(), success: false, error },
        ]);
        throw new Error(error);
      }

      try {
        logger.log('Attempting login with credentials');
        const response = await authService.login(credentials);

        if (response.success) {
          let accessToken, refreshToken, userData;

          // Handle different response structures
          if (response.data) {
            accessToken = response.data.accessToken;
            refreshToken = response.data.refreshToken;
            userData = response.data.user;
          } else if (
            'accessToken' in response &&
            'refreshToken' in response &&
            'user' in response
          ) {
            const responseData = response as {
              accessToken: string;
              refreshToken?: string;
              user: {
                id?: string;
                _id?: string;
                name: string;
                email?: string;
                phoneNumber: string;
              };
            };
            accessToken = responseData.accessToken;
            refreshToken = responseData.refreshToken;
            userData = responseData.user;
          }

          if (accessToken && refreshToken && userData) {
            await login(accessToken, refreshToken, userData);
          } else {
            throw new Error('Invalid login response structure');
          }
        }

        return response;
      } catch (error: unknown) {
        logger.error('Login with credentials failed:', error);

        // Check if it's a concurrent login error (other device login)
        if (
          error instanceof Error &&
          (error.message.includes('concurrent') ||
            error.message.includes('another device') ||
            error.message.includes('multiple sessions'))
        ) {
          if (logoutRef.current) {
            await logoutRef.current('other_device_login');
          }
          setSessionTimeoutReason('other_device_login');
          setShowSessionTimeoutModal(true);
        }

        // Record failed login attempt
        setLoginAttempts((prev) => [
          ...prev.filter((attempt) => Date.now() - attempt.timestamp < LOGIN_ATTEMPT_WINDOW),
          {
            timestamp: Date.now(),
            success: false,
            error: error instanceof Error ? error.message : 'Unknown error',
          },
        ]);

        throw error;
      }
    },
    [loginAttempts, login, LOGIN_ATTEMPT_WINDOW],
  );

  // Auth service wrapper methods
  const register = useCallback(async (userData: RegisterRequest) => {
    return await authService.register(userData);
  }, []);

  const sendOTP = useCallback(async (phoneData: SendOTPRequest) => {
    return await authService.sendOTP(phoneData);
  }, []);

  const verifyOTP = useCallback(async (otpData: VerifyOTPRequest) => {
    return await authService.verifyOTP(otpData);
  }, []);

  const forgotPassword = useCallback(async (resetData: ForgotPasswordRequest) => {
    return await authService.forgotPassword(resetData);
  }, []);

  const resetPassword = useCallback(async (resetData: ResetPasswordRequest) => {
    return await authService.resetPassword(resetData);
  }, []);

  // Role switching
  const switchRole = useCallback(
    async (role: 'user' | 'merchant') => {
      try {
        if (!user) {
          throw new Error('No user is logged in');
        }

        // Check if user has merchantId for merchant role switch
        if (role === 'merchant' && !user.merchantId) {
          throw new Error(
            'You do not have a merchant account. Please contact support to set up your merchant account.',
          );
        }

        const response = await authService.switchRole(role);
        if (!response.success) {
          // Handle specific merchant profile inactive response
          if (
            response.error === 'Merchant profile inactive' ||
            (response as SwitchRoleResponse).reason === 'deactivated_by_admin' ||
            response.error?.toLowerCase().includes('merchant profile inactive')
          ) {
            // Set the reason and show account disabled modal for merchant profile inactive
            setAccountDisableReason(
              (response as SwitchRoleResponse).reason || 'deactivated_by_admin',
            );
            setShowAccountDisableModal(true);
            return;
          }
          // Handle other 403 errors or disabled accounts
          if (
            response.error?.includes('403') ||
            response.error?.toLowerCase().includes('forbidden') ||
            response.error?.toLowerCase().includes('disabled')
          ) {
            // Show account disabled modal instead of window.confirm
            setAccountDisableReason(undefined);
            setShowAccountDisableModal(true);
            return;
          }
          throw new Error(response.error || 'Role switch failed');
        }

        const newAccessToken = response.accessToken;
        const newRefreshToken = response.refreshToken;
        const newUser = response.user || {
          id: user.id,
          name: user.name,
          email: user.email,
          phoneNumber: user.phoneNumber,
          role: '',
          activeRole: role,
          merchantId: user.merchantId,
        };

        if (!newAccessToken) {
          throw new Error('No access token returned from switch-role');
        }

        await tokenStorage.setTokens(
          newAccessToken,
          newRefreshToken || tokenStorage.getRefreshToken() || '',
          {
            id: newUser.id || user.id || '',
            name: newUser.name,
            email: newUser.email || '',
            phoneNumber: newUser.phoneNumber,
            activeRole:
              ((newUser as UserApiResponse).activeRole as 'user' | 'merchant') || role || 'user',
            merchantId: (newUser as UserApiResponse).merchantId || user.merchantId,
            walletBalance: (newUser as UserApiResponse).walletBalance || user.walletBalance,
          },
        );

        Cookies.set('accessToken', newAccessToken, {
          secure: window.location.protocol === 'https:',
          sameSite: 'lax',
          expires: 7,
          path: '/',
        });
        if (newRefreshToken) {
          Cookies.set('refreshToken', newRefreshToken, {
            secure: window.location.protocol === 'https:',
            sameSite: 'lax',
            expires: 7,
            path: '/',
          });
        }
        Cookies.set(
          'user',
          JSON.stringify({
            id: newUser.id || user.id || '',
            name: newUser.name,
            email: newUser.email || '',
            phoneNumber: newUser.phoneNumber,
            activeRole:
              ((newUser as UserApiResponse).activeRole as 'user' | 'merchant') || role || 'user',
            merchantId: (newUser as UserApiResponse).merchantId || user.merchantId,
            walletBalance: (newUser as UserApiResponse).walletBalance || user.walletBalance,
          }),
          {
            secure: window.location.protocol === 'https:',
            sameSite: 'lax',
            expires: 7,
            path: '/',
          },
        );

        setUser({
          id: newUser.id || user.id || '',
          name: newUser.name,
          email: newUser.email || '',
          phoneNumber: newUser.phoneNumber,
          activeRole:
            ((newUser as UserApiResponse).activeRole as 'user' | 'merchant') || role || 'user',
          merchantId: (newUser as UserApiResponse).merchantId || user.merchantId,
          walletBalance: (newUser as UserApiResponse).walletBalance || user.walletBalance,
        });

        logger.log('Active role switched to', role, 'and tokens updated');

        // Refresh user data after role switch
        try {
          logger.log('🔄 Refreshing user data after role switch...');

          // Initialize updatedUser with current user data as fallback
          let updatedUser = {
            id: user.id || '',
            name: user.name || '',
            email: user.email || '',
            phoneNumber: user.phoneNumber || '',
            activeRole: role || '',
            merchantId: user.merchantId,
            walletBalance: user.walletBalance,
          };

          // 1. Fetch fresh user profile data
          const profileResponse = await authService.getUserProfile();
          if (profileResponse.success && profileResponse.data) {
            const freshUserData = profileResponse.data;

            // Update user state with fresh profile data
            updatedUser = {
              id: freshUserData.id || freshUserData._id || user.id || '',
              name: freshUserData.name,
              email: freshUserData.email || '',
              phoneNumber: freshUserData.phoneNumber,
              activeRole:
                ((freshUserData as UserApiResponse).activeRole as 'user' | 'merchant') || '',
              merchantId: (freshUserData as UserApiResponse).merchantId || user.merchantId,
              walletBalance: (freshUserData as UserApiResponse).walletBalance,
            };

            // Update stored user data
            await tokenStorage.setTokens(
              newAccessToken,
              newRefreshToken || tokenStorage.getRefreshToken() || '',
              updatedUser,
            );

            // Update user cookie with fresh data
            Cookies.set('user', JSON.stringify(updatedUser), {
              secure: window.location.protocol === 'https:',
              sameSite: 'lax',
              expires: 7,
              path: '/',
            });

            logger.log('✅ User profile data refreshed successfully');
          } else {
            logger.warn('⚠️ Failed to refresh user profile data:', profileResponse.error);
          }

          // Update user state with the final user data
          setUser(updatedUser);

          // 2. Fetch merchant status if switching to merchant role
          if (role === 'merchant') {
            try {
              logger.log('🏪 Fetching merchant status after role switch...');
              const merchantStatusResponse = await merchantService.getMerchantStatus();
              if (merchantStatusResponse.success) {
                logger.log(
                  '✅ Merchant status fetched successfully:',
                  merchantStatusResponse.status,
                );
              } else {
                logger.warn('⚠️ Failed to fetch merchant status:', merchantStatusResponse.error);
              }
            } catch (merchantError) {
              logger.warn('⚠️ Error fetching merchant status:', merchantError);
              // Don't throw here - the role switch itself was successful
            }
          }

          // Simple page reload after successful role switch
          logger.log('✅ Role switch completed successfully, reloading page...');

          // Store success message for post-reload toast
          const roleDisplayName = role === 'merchant' ? 'Merchant' : 'User';
          localStorage.setItem(
            'postReloadToast',
            JSON.stringify({
              type: 'success',
              message: `Switched to ${roleDisplayName} account`,
              timestamp: Date.now(),
            }),
          );

          window.location.reload();
        } catch (refreshError) {
          logger.warn('⚠️ Failed to refresh some user data after role switch:', refreshError);
          // Don't throw here - the role switch itself was successful
          // Still reload the page to ensure UI consistency

          // Store success message for post-reload toast even if refresh failed
          const roleDisplayName = role === 'merchant' ? 'Merchant' : 'User';
          localStorage.setItem(
            'postReloadToast',
            JSON.stringify({
              type: 'success',
              message: `Switched to ${roleDisplayName} account`,
              timestamp: Date.now(),
            }),
          );

          window.location.reload();
        }
      } catch (error) {
        logger.error('Failed to switch role:', error);
        throw error;
      }
    },
    [user],
  );

  // Session timeout modal handlers
  const handleSessionTimeoutModalClose = useCallback(() => {
    setShowSessionTimeoutModal(false);
  }, []);

  const handleSessionTimeoutRetry = useCallback(async () => {
    setShowSessionTimeoutModal(false);
    // For now, just close the modal - the user can manually login
    // TODO: Consider alternative approach for opening login dialog
  }, []);

  // Account disable modal handlers
  const handleAccountDisableModalClose = useCallback(() => {
    setShowAccountDisableModal(false);
    setAccountDisableReason(undefined);
  }, []);

  const handleAccountDisableModalConfirm = useCallback(async () => {
    setShowAccountDisableModal(false);
    setAccountDisableReason(undefined);
    // Switch back to user role
    try {
      const userResponse = await authService.switchRole('user');
      if (userResponse.success) {
        window.location.reload();
      }
    } catch (userSwitchError) {
      logger.error('Failed to switch back to user role:', userSwitchError);
      window.location.reload();
    }
  }, []);

  // Update favicon based on activeRole (runs after reloads as well)
  useEffect(() => {
    updateFaviconForRole((user?.activeRole as 'user' | 'merchant' | undefined) || undefined);
  }, [user?.activeRole]);

  return (
    <>
      <AuthContext.Provider
        value={{
          isAuthenticated,
          user,
          isLoading,
          login,
          logout,
          loginWithCredentials,
          register,
          sendOTP,
          verifyOTP,
          forgotPassword,
          resetPassword,
          switchRole,
          // Enhanced features
          refreshToken: () => refreshToken(true),
          refreshBalance,
          makeAuthenticatedRequest,
          networkStatus: {
            isOnline: networkStatus.isOnline,
            error: networkStatus.error,
          },
          lastActivity,
        }}
      >
        {children}
      </AuthContext.Provider>

      <SessionTimeoutModal
        isOpen={showSessionTimeoutModal}
        onClose={handleSessionTimeoutModalClose}
        onRetry={handleSessionTimeoutRetry}
        reason={sessionTimeoutReason}
      />

      <AccountDisableModal
        isOpen={showAccountDisableModal}
        onClose={handleAccountDisableModalClose}
        onConfirm={handleAccountDisableModalConfirm}
        reason={accountDisableReason}
      />
    </>
  );
}
